import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Subject, timer, of, EMPTY, concat } from 'rxjs';
import {
  takeUntil,
  switchMap,
  filter,
  takeWhile,
  tap,
  delay,
  catchError,
} from 'rxjs/operators';

/* NG Zorro */
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';

/* Rapider SDKs (Ensure these match your actual SDK paths) */
import {
  ProjectVersionControllerService,
  ApplicationBuilderControllerService,
  ProjectCodeGenerationControllerService,
  ProjectDeployControllerService,
  GitInfoControllerService,
} from '@rapider/sdk';

import {
  DEV_AGENT_CONFIG,
  DEV_AGENT_CAPABILITIES,
  DEV_AGENT_ACTION_OPTIONS,
  DevAgentAction,
  DevAgentStepState,
  buildProcessStatusFilter,
  unwrapApiResponse,
  formatSubProcesses,
  parseDeploymentLogs,
  getDevAgentStepState,
  computeDevAgentProgress,
  getDevAgentRunStatus,
} from './dev-agent.config';
import { DevAgentActions } from './dev-agent.actions';

/**
 * Component responsible for orchestrating the Dev Agent code generation and deployment lifecycle.
 * Automatically listens to NGRX actions to mount, start, minimize, and close itself.
 */
@Component({
  selector: 'rapider-dev-agent',
  standalone: true,
  imports: [
    CommonModule,
    NzCardModule,
    NzTimelineModule,
    NzButtonModule,
    NzSpinModule,
    NzDescriptionsModule,
    NzTooltipModule,
    NzModalModule,
    NzTagModule,
    NzCardModule,
    NzTimelineModule,
    NzCollapseModule,
    FormsModule,
  ],
  templateUrl: './dev-agent.component.html',
  styleUrls: ['./dev-agent.component.css'],
})
export class DevAgentComponent implements OnInit, OnDestroy {
  public readonly config = DEV_AGENT_CONFIG;
  /** Capability tiles explaining what the agent can do. */
  public readonly capabilities = DEV_AGENT_CAPABILITIES;
  /** Job option cards for the configuration step. */
  public readonly actionOptions = DEV_AGENT_ACTION_OPTIONS;
  private readonly destroy$ = new Subject<void>();
  /** Emits when the user closes the drawer mid-run so client-side polling stops. */
  private readonly runCancel$ = new Subject<void>();

  // Injectables
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly projectVersionService = inject(
    ProjectVersionControllerService,
  );
  private readonly appBuilderService = inject(
    ApplicationBuilderControllerService,
  );
  private readonly codeGenService = inject(
    ProjectCodeGenerationControllerService,
  );
  private readonly deployService = inject(ProjectDeployControllerService);
  private readonly gitInfoService = inject(GitInfoControllerService);
  private readonly modalService = inject(NzModalService);

  // Configuration Signals
  public showConfig = signal<boolean>(true);
  public selectedAction = signal<DevAgentAction>('generateAndDeploy');
  public selectedBranch = signal<string>('');
  public backendContainers = signal<any[]>([]);
  public isLoadingBackendStatus = signal<boolean>(false);
  public skippedSteps = signal<number[]>([]);
  public recentDeployments = signal<any[]>([]);
  public isLoadingDeployments = signal<boolean>(false);

  // State Signals
  public isVisible = signal<boolean>(false);
  public isMinimized = signal<boolean>(false);
  public activeStepId = signal<number>(0);
  public subProcesses = signal<any[]>([]);
  public isComplete = signal<boolean>(false);
  public hasError = signal<boolean>(false);
  public errorMessage = signal<string>('');
  public serverInfo = signal<any>(null);
  public projectVersion = signal<any>(null);
  public deploymentId = signal<string>(''); // Added to track the current deployment process ID

  // Log Modal Signals
  public isLogsModalVisible = signal<boolean>(false);
  public isLoadingLogs = signal<boolean>(false);
  public logsContent = signal<string>('');
  public workflowRunTitle = signal<string>('');
  public workflowRunStatus = signal<string>('');
  public showLogTimestamps = signal<boolean>(true); // NEW: Controls timestamp visibility

  // Computed Values
  public processedSubProcesses = computed(() =>
    formatSubProcesses(this.subProcesses()),
  );
  public parsedLogs = computed(() => parseDeploymentLogs(this.logsContent())); // NEW: Parses the raw string dynamically

  /** Overall run status for the drawer header pill. */
  public readonly runStatus = computed(() =>
    getDevAgentRunStatus(this.showConfig(), this.isComplete(), this.hasError()),
  );

  /** True while the orchestration is actively running. */
  public readonly isRunning = computed(() => this.runStatus() === 'running');

  /** Overall progress (0–100) of the current run, ignoring skipped steps. */
  public readonly progressPercent = computed(() =>
    computeDevAgentProgress(
      this.config.steps.map((s) => s.id),
      this.activeStepId(),
      this.isComplete(),
      this.skippedSteps(),
    ),
  );

  /** Number of steps that are part of the selected job. */
  public readonly relevantStepCount = computed(
    () =>
      this.config.steps.filter((s) => !this.skippedSteps().includes(s.id))
        .length,
  );

  /** 1-based position of the active step among the relevant steps. */
  public readonly relevantStepPosition = computed(() => {
    const relevant = this.config.steps.filter(
      (s) => !this.skippedSteps().includes(s.id),
    );
    const idx = relevant.findIndex((s) => s.id === this.activeStepId());
    return idx === -1 ? relevant.length : idx + 1;
  });

  /**
   * Returns the visual state of a step for the custom stepper.
   * @param {number} stepId - Step id.
   * @returns {DevAgentStepState} The step state.
   */
  public getStepState(stepId: number): DevAgentStepState {
    return getDevAgentStepState(
      stepId,
      this.activeStepId(),
      this.isComplete(),
      this.hasError(),
      this.skippedSteps(),
    );
  }

  /**
   * Returns the sub-processes that belong to a given step (4 = code, 5 = deploy).
   * @param {number} stepId - Step id.
   * @returns {any[]} Matching sub-processes.
   */
  public getStepSubProcesses(stepId: number): any[] {
    if (stepId !== 4 && stepId !== 5) return [];
    if (this.skippedSteps().includes(stepId)) return [];
    if (this.activeStepId() < stepId) return [];
    const needle = stepId === 4 ? 'code' : 'deploy';
    return this.processedSubProcesses().filter((p) =>
      String(p?.key ?? '').includes(needle),
    );
  }

  ngOnInit(): void {
    // Listen to global NGRX actions to control the lifecycle seamlessly
    this.actions$
      .pipe(ofType(DevAgentActions.triggerAgent), takeUntil(this.destroy$))
      .subscribe(() => {
        this.resetState();
        this.isVisible.set(true);
        this.isMinimized.set(false);
        this.showConfig.set(true);
        this.fetchBackendStatus();
        this.fetchRecentDeployments();
      });

    this.actions$
      .pipe(ofType(DevAgentActions.minimizeAgent), takeUntil(this.destroy$))
      .subscribe(() => this.isMinimized.set(true));
    this.actions$
      .pipe(ofType(DevAgentActions.restoreAgent), takeUntil(this.destroy$))
      .subscribe(() => this.isMinimized.set(false));
    this.actions$
      .pipe(ofType(DevAgentActions.closeAgent), takeUntil(this.destroy$))
      .subscribe(() => {
        // Stop any client-side polling of a run that is still in flight
        this.runCancel$.next();
        this.isVisible.set(false);
        this.resetState();
      });
  }

  ngOnDestroy(): void {
    this.runCancel$.next();
    this.runCancel$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Resets the agent's internal state for a fresh run.
   */
  private resetState(): void {
    this.activeStepId.set(0);
    this.subProcesses.set([]);
    this.isComplete.set(false);
    this.hasError.set(false);
    this.errorMessage.set('');
    this.serverInfo.set(null);
    this.projectVersion.set(null);
    this.deploymentId.set('');
    this.isLogsModalVisible.set(false);
    this.logsContent.set('');
    this.showLogTimestamps.set(true);
    this.skippedSteps.set([]);
  }

  /**
   * Fetches the backend server containers for the current active project.
   */
  public async fetchBackendStatus(): Promise<void> {
    try {
      this.isLoadingBackendStatus.set(true);
      const containers = await import('rxjs').then((m) =>
        m.firstValueFrom(this.appBuilderService.monitorApplication()),
      );
      this.backendContainers.set(containers || []);
    } catch (error) {
      console.error('Failed to fetch backend status:', error);
      this.backendContainers.set([]);
    } finally {
      this.isLoadingBackendStatus.set(false);
    }
  }

  /**
   * Fetches the last 5 deployment records to display recent activity.
   */
  public async fetchRecentDeployments(): Promise<void> {
    try {
      this.isLoadingDeployments.set(true);
      const deployments = await import('rxjs').then((m) =>
        m.firstValueFrom(
          this.deployService.find({
            filter: { order: ['createdDate DESC'], limit: 5 },
          }),
        ),
      );
      this.recentDeployments.set(Array.isArray(deployments) ? deployments : []);
    } catch (error) {
      console.error('Failed to fetch recent deployments:', error);
      this.recentDeployments.set([]);
    } finally {
      this.isLoadingDeployments.set(false);
    }
  }

  /**
   * Validates the configuration and starts the orchestration.
   * If code generation is involved, prompts the user to confirm overwriting.
   */
  public startConfiguredAgent(): void {
    const action = this.selectedAction();
    if (action === 'generateAndDeploy' || action === 'generateOnly') {
      this.modalService.confirm({
        nzTitle: 'Regenerate Code',
        nzContent:
          'Are you sure you want to regenerate the code? Any custom code modifications in the branch may be overwritten.',
        nzOkText: 'Yes, Regenerate',
        nzCancelText: 'Cancel',
        nzOnOk: () => this.proceedWithOrchestration(),
      });
    } else {
      this.proceedWithOrchestration();
    }
  }

  private proceedWithOrchestration(): void {
    this.showConfig.set(false);

    const action = this.selectedAction();
    if (action === 'deployOnly') {
      this.skippedSteps.set([1, 2, 3, 4]);
    } else if (action === 'generateOnly') {
      this.skippedSteps.set([5, 6]);
    } else {
      this.skippedSteps.set([]);
    }

    this.startAgentOrchestration();
  }

  /**
   * Executes the strict 6-step RxJS orchestration chain for generating and deploying code.
   * Leverages `concat` for sequential execution of delayed simulated steps and actual polling API calls.
   */
  private startAgentOrchestration(): void {
    const startTimeIso = new Date().toISOString();
    localStorage.setItem(this.config.storageKey, startTimeIso);
    const action = this.selectedAction();

    const orchestrationSteps: any[] = [];

    if (action !== 'deployOnly') {
      orchestrationSteps.push(
        // Step 1: Analyzing (Simulated Delay)
        of(null).pipe(
          tap(() => this.activeStepId.set(1)),
          delay(this.config.simulatedDelayMs),
        ),

        // Step 2: Validating Version (API call)
        of(null).pipe(
          tap(() => this.activeStepId.set(2)),
          switchMap(() =>
            this.projectVersionService.find({
              filter: { order: ['createdDate DESC'], limit: 1 },
            }),
          ),
          tap((res) => this.projectVersion.set(unwrapApiResponse(res))),
          delay(this.config.simulatedDelayMs),
        ),

        // Step 3: Migrating DB (Simulated Delay)
        of(null).pipe(
          tap(() => this.activeStepId.set(3)),
          delay(this.config.simulatedDelayMs),
        ),

        // Step 4: Generating Code (Trigger + Poll)
        of(null).pipe(
          tap(() => this.activeStepId.set(4)),
          switchMap(() => this.appBuilderService.generateCode()),
          switchMap(() => this.pollCodeGenerationStatus(startTimeIso)),
        ),
      );
    } else {
      // Fast forward the active step to skip the generation phase visually
      orchestrationSteps.push(
        of(null).pipe(tap(() => this.activeStepId.set(4))),
      );
    }

    if (action !== 'generateOnly') {
      orchestrationSteps.push(
        // Step 5: Deploying Server (Trigger + Poll)
        of(null).pipe(
          tap(() => this.activeStepId.set(5)),
          switchMap(() =>
            this.appBuilderService.deploy({
              branch: this.selectedBranch() || undefined,
            }),
          ),
          tap((deployResponse: any) => {
            // Track the deployment ID locally so we can fetch logs later if requested
            const unwrapped = unwrapApiResponse(deployResponse);
            if (unwrapped?.id) {
              this.deploymentId.set(unwrapped.id);
            }
          }),
          switchMap((deployResponse: any) =>
            this.pollDeploymentStatus(unwrapApiResponse(deployResponse)?.id),
          ),
        ),

        // Step 6: Starting the server (Poll Verification)
        of(null).pipe(
          tap(() => this.activeStepId.set(6)),
          switchMap(() => this.pollApplicationRunning()),
        ),
      );
    } else {
      // Fast forward active step
      orchestrationSteps.push(
        of(null).pipe(tap(() => this.activeStepId.set(6))),
      );
    }

    concat(...orchestrationSteps)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.runCancel$),
        catchError((err) => {
          console.error('Rapider Dev Agent Process Failed:', err);
          this.hasError.set(true);
          this.errorMessage.set(
            err?.message ||
              'An unexpected error occurred during orchestration.',
          );
          return EMPTY; // Stop the stream safely
        }),
      )
      .subscribe({
        complete: () => {
          if (!this.hasError()) {
            this.isComplete.set(true);
          }
        },
      });
  }

  /**
   * Polls the code generation endpoint every 10 seconds.
   */
  private pollCodeGenerationStatus(startTime: string) {
    const filterQuery = buildProcessStatusFilter(startTime);
    return timer(0, this.config.pollingIntervalMs).pipe(
      switchMap(() => this.codeGenService.find({ filter: filterQuery })),
      tap((res) => {
        const unwrapped = unwrapApiResponse(res);
        if (unwrapped?.processes) this.subProcesses.set(unwrapped.processes);
      }),
      takeWhile((res) => {
        const unwrapped = unwrapApiResponse(res);
        if (!unwrapped) return true;
        if (unwrapped.status === 'failure')
          throw new Error('Code generation process failed on the server.');
        return unwrapped.status !== 'successful';
      }, true),
      filter((res) => unwrapApiResponse(res)?.status === 'successful'),
    );
  }

  /**
   * Polls the deployment status endpoint every 10 seconds.
   */
  private pollDeploymentStatus(deploymentId: string) {
    if (!deploymentId) throw new Error('Invalid Deployment ID received.');
    return timer(0, this.config.pollingIntervalMs).pipe(
      switchMap(() => this.deployService.findById({ id: deploymentId })),
      tap((res) => {
        const unwrapped = unwrapApiResponse(res);
        if (unwrapped?.processes) {
          this.subProcesses.update((curr) => {
            // Merge processes dynamically to show continuous timeline
            const newProcs = unwrapped.processes.filter(
              (p: any) => !curr.some((c) => c.timestamp === p.timestamp),
            );
            return [...curr, ...newProcs];
          });
        }
      }),
      takeWhile((res) => {
        const unwrapped = unwrapApiResponse(res);
        if (unwrapped?.status === 'failure')
          throw new Error('Deployment process failed on the server.');
        return unwrapped?.status !== 'successful';
      }, true),
      filter((res) => unwrapApiResponse(res)?.status === 'successful'),
    );
  }

  /**
   * Polls the application monitoring endpoint until the container State is 'running'.
   */
  private pollApplicationRunning() {
    return timer(0, this.config.pollingIntervalMs).pipe(
      switchMap(() => this.appBuilderService.monitorApplication()),
      tap((res) => {
        const unwrapped = unwrapApiResponse(res);
        if (unwrapped) this.serverInfo.set(unwrapped);
      }),
      takeWhile((res) => {
        const unwrapped = unwrapApiResponse(res);
        return unwrapped?.State !== 'running';
      }, true),
      filter((res) => unwrapApiResponse(res)?.State === 'running'),
    );
  }

  /**
   * Fetches the deployment logs via the Application Builder API using the current deploymentId.
   * Displays the returned raw string logs in the full-screen modal.
   * @returns {void}
   */
  public fetchAndShowLogs(): void {
    const currentDeploymentId = this.deploymentId();

    this.isLoadingLogs.set(true);
    this.isLogsModalVisible.set(true);
    this.workflowRunTitle.set('Server Process');
    this.workflowRunStatus.set(''); // Clear status since simple string logs don't have a structured status

    if (!currentDeploymentId) {
      this.logsContent.set(this.config.texts.noDeploymentIdError);
      this.isLoadingLogs.set(false);
      return;
    }

    this.appBuilderService
      .getDeploymentLogs({ id: currentDeploymentId })
      .pipe(
        takeUntil(this.destroy$),
        catchError((err) => {
          console.error('Failed to fetch deployment logs:', err);
          this.logsContent.set(
            'Failed to fetch deployment logs. Please try again or check your server configuration.',
          );
          this.isLoadingLogs.set(false);
          return EMPTY;
        }),
      )
      .subscribe((logsRes: any) => {
        const logs = unwrapApiResponse(logsRes);
        // The API returns a full string; if it accidentally returns an object, we stringify it.
        this.logsContent.set(
          typeof logs === 'string' ? logs : JSON.stringify(logs, null, 2),
        );
        this.isLoadingLogs.set(false);
      });
  }

  /**
   * Toggles the visibility of timestamps in the log viewer.
   */
  public toggleTimestamps(): void {
    this.showLogTimestamps.update((val) => !val);
  }

  /**
   * Dispatches NGRX action to minimize the component.
   */
  public minimizeAgent(): void {
    this.store.dispatch(DevAgentActions.minimizeAgent());
  }

  /**
   * Dispatches NGRX action to destroy/close the component.
   */
  public closeAgent(): void {
    this.store.dispatch(DevAgentActions.closeAgent());
  }

  /**
   * Closes the drawer. While a run is in progress the user is asked to confirm,
   * since closing stops tracking the run (minimizing keeps it).
   */
  public requestClose(): void {
    if (!this.isRunning()) {
      this.closeAgent();
      return;
    }
    this.modalService.confirm({
      nzTitle: this.config.texts.closeWhileRunningTitle,
      nzContent: this.config.texts.closeWhileRunningContent,
      nzOkText: this.config.texts.closeWhileRunningOk,
      nzOkDanger: true,
      nzCancelText: this.config.texts.closeWhileRunningCancel,
      nzOnOk: () => this.closeAgent(),
    });
  }
}
